/**
 * Cart Enhancements for Pinnacle/Bastion Theme
 * - Syncs drop location select to Shopify cart attributes
 * - Adds a delivery date calendar picker (Flatpickr)
 * - Blocks Alberta statutory holidays automatically
 * - Persists all custom fields into cart attributes so they appear on the order
 *
 * Dependencies:
 *   Flatpickr CSS + JS (loaded automatically if not already present)
 *
 * Usage:
 *   Include this script on the cart page after the cart form:
 *   <script src="{{ 'cart-form.js' | asset_url }}" defer></script>
 */

(function () {
  "use strict";

  /* -------------------------------------------------------------------------
   * Config
   * ---------------------------------------------------------------------- */

  var FLATPICKR_CSS =
    "https://cdn.jsdelivr.net/npm/flatpickr/dist/flatpickr.min.css";
  var FLATPICKR_JS = "https://cdn.jsdelivr.net/npm/flatpickr";

  // Minimum number of days from today before a delivery can be scheduled
  var MIN_LEAD_DAYS = 2;

  // Days of the week the store delivers (0 = Sunday, 1 = Mon … 6 = Sat)
  var DELIVERY_DAYS = [1, 2, 3, 4, 5, 6];

  // Maximum number of days into the future customers can book.
  // Set to 0 or false to allow unlimited future dates.
  var MAX_FUTURE_DAYS = 0;

  /* -------------------------------------------------------------------------
   * Alberta Statutory Holidays (computed automatically each year)
   * ---------------------------------------------------------------------- */

  function nthWeekday(year, month, dayOfWeek, n) {
    var date = new Date(year, month, 1);
    var count = 0;
    while (count < n) {
      if (date.getDay() === dayOfWeek) {
        count++;
      }
      if (count < n) {
        date.setDate(date.getDate() + 1);
      }
    }
    return date;
  }

  function getGoodFriday(year) {
    var a = year % 19;
    var b = Math.floor(year / 100);
    var c = year % 100;
    var d = Math.floor(b / 4);
    var e = b % 4;
    var f = Math.floor((b + 8) / 25);
    var g = Math.floor((b - f + 1) / 3);
    var h = (19 * a + b - d - g + 15) % 30;
    var i = Math.floor(c / 4);
    var k = c % 4;
    var l = (32 + 2 * e + 2 * i - h - k) % 7;
    var m = Math.floor((a + 11 * h + 22 * l) / 451);
    var month = Math.floor((h + l - 7 * m + 114) / 31) - 1;
    var day = ((h + l - 7 * m + 114) % 31) + 1;
    var easter = new Date(year, month, day);
    easter.setDate(easter.getDate() - 2);
    return easter;
  }

  function pad(n) {
    return n < 10 ? "0" + n : "" + n;
  }

  function fmtDate(d) {
    return (
      d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate())
    );
  }

  function getAlbertaHolidays(year) {
    var holidays = [];
    var may25, victoria;

    // New Year's Day
    holidays.push(fmtDate(new Date(year, 0, 1)));
    // Family Day — 3rd Monday of February
    holidays.push(fmtDate(nthWeekday(year, 1, 1, 3)));
    // Good Friday
    holidays.push(fmtDate(getGoodFriday(year)));
    // Victoria Day — last Monday before May 25
    may25 = new Date(year, 4, 25);
    victoria = new Date(may25);
    while (victoria.getDay() !== 1) {
      victoria.setDate(victoria.getDate() - 1);
    }
    holidays.push(fmtDate(victoria));
    // Canada Day
    holidays.push(fmtDate(new Date(year, 6, 1)));
    // Heritage Day — 1st Monday of August
    holidays.push(fmtDate(nthWeekday(year, 7, 1, 1)));
    // Labour Day — 1st Monday of September
    holidays.push(fmtDate(nthWeekday(year, 8, 1, 1)));
    // National Day for Truth and Reconciliation
    holidays.push(fmtDate(new Date(year, 8, 30)));
    // Thanksgiving — 2nd Monday of October
    holidays.push(fmtDate(nthWeekday(year, 9, 1, 2)));
    // Remembrance Day
    holidays.push(fmtDate(new Date(year, 10, 11)));
    // Christmas Day
    holidays.push(fmtDate(new Date(year, 11, 25)));

    return holidays;
  }

  function buildHolidaySet() {
    var thisYear = new Date().getFullYear();
    var all = getAlbertaHolidays(thisYear).concat(
      getAlbertaHolidays(thisYear + 1),
    );
    var set = {};
    var i;
    for (i = 0; i < all.length; i++) {
      set[all[i]] = true;
    }
    return set;
  }

  var holidaySet = buildHolidaySet();

  function isHoliday(date) {
    return !!holidaySet[fmtDate(date)];
  }

  /* -------------------------------------------------------------------------
   * Helpers
   * ---------------------------------------------------------------------- */

  function updateCartAttributes(attributes) {
    return fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ attributes: attributes }),
    }).then(function (res) {
      if (!res.ok) {
        throw new Error("Cart update failed: " + res.status);
      }
      return res.json();
    });
  }

  function loadCSS(href) {
    if (document.querySelector('link[href="' + href + '"]')) {
      return;
    }
    var link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = href;
    document.head.appendChild(link);
  }

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      if (document.querySelector('script[src="' + src + '"]')) {
        resolve();
        return;
      }
      var script = document.createElement("script");
      script.src = src;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  /* -------------------------------------------------------------------------
   * Drop Location — connect the existing <select> to cart attributes
   * ---------------------------------------------------------------------- */

  function initDropLocation() {
    var select = document.getElementById("CartDropLocation");
    if (!select) {
      return;
    }

    select.name = "attributes[Drop Location]";

    select.addEventListener("change", function () {
      var value = select.value;
      if (value === "Select a Location") {
        value = "";
      }
      updateCartAttributes({ "Drop Location": value });
    });
  }

  /* -------------------------------------------------------------------------
   * Delivery Date — inject a date picker after the drop location select
   * ---------------------------------------------------------------------- */

  function buildDeliveryDateUI() {
    var anchor = document.getElementById("CartDropLocation");
    if (!anchor) {
      return null;
    }

    // Avoid duplicating the UI if it already exists in the new DOM
    var existing = document.getElementById("CartDeliveryDate");
    if (existing) {
      return existing;
    }

    var helperText = anchor.nextElementSibling;
    while (helperText && helperText.tagName !== "P") {
      helperText = helperText.nextElementSibling;
    }
    var insertAfter = helperText || anchor;

    var label = document.createElement("label");
    label.setAttribute("for", "CartDeliveryDate");
    label.className = "thb-cart-form--notes-title";
    label.textContent = "Preferred delivery date *";

    var input = document.createElement("input");
    input.type = "text";
    input.id = "CartDeliveryDate";
    input.name = "attributes[Delivery Date]";
    input.className = "full";
    input.placeholder = "Select a delivery date";
    input.readOnly = true;
    input.style.cssText = "width:100%;cursor:pointer;margin-bottom:20px;";

    insertAfter.parentNode.insertBefore(input, insertAfter.nextSibling);
    insertAfter.parentNode.insertBefore(label, input);

    return input;
  }

  function initDeliveryDate() {
    var input = buildDeliveryDateUI();
    if (!input) {
      return;
    }

    // If Flatpickr already initialized on this element, destroy and re-init
    if (input._flatpickr) {
      input._flatpickr.destroy();
    }

    var minDate = new Date();
    minDate.setDate(minDate.getDate() + MIN_LEAD_DAYS);

    loadCSS(FLATPICKR_CSS);

    loadScript(FLATPICKR_JS).then(function () {
      var options = {};

      options.dateFormat = "F j, Y";
      options.minDate = minDate;
      options.disableMobile = true;

      options.disable = [
        function (date) {
          if (DELIVERY_DAYS.indexOf(date.getDay()) === -1) {
            return true;
          }
          if (isHoliday(date)) {
            return true;
          }
          return false;
        },
      ];

      options.onChange = function (selectedDates, dateStr) {
        updateCartAttributes({ "Delivery Date": dateStr });
      };

      if (MAX_FUTURE_DAYS) {
        options.maxDate = new Date().fp_incr(MAX_FUTURE_DAYS);
      }

      flatpickr(input, options);
    });
  }

  /* -------------------------------------------------------------------------
   * Form validation — block checkout if required fields are empty
   * ---------------------------------------------------------------------- */

  function initValidation() {
    var form = document.getElementById("cart-form");
    if (!form) {
      return;
    }

    var checkoutButtons = document.querySelectorAll('[name="checkout"]');

    checkoutButtons.forEach(function (btn) {
      btn.addEventListener("click", function (e) {
        var dropLocation = document.getElementById("CartDropLocation");
        var deliveryDate = document.getElementById("CartDeliveryDate");
        var messages = [];

        if (
          dropLocation &&
          (!dropLocation.value || dropLocation.value === "Select a Location")
        ) {
          messages.push("Please select a drop location.");
        }
        if (deliveryDate && !deliveryDate.value) {
          messages.push("Please select a preferred delivery date.");
        }

        if (messages.length) {
          e.preventDefault();
          alert(messages.join("\n"));
        }
      });
    });
  }

  /* -------------------------------------------------------------------------
   * Boot — run once, then re-run whenever the cart DOM is replaced
   * ---------------------------------------------------------------------- */

  function init() {
    initDropLocation();
    initDeliveryDate();
    initValidation();
  }

  function observeCartForm() {
    // Watch for Shopify's AJAX cart re-rendering the cart section.
    // The observer fires when child nodes are added/removed inside the
    // cart form (or its parent section), which happens after quantity
    // changes or item removals trigger a section re-render.
    var target =
      document.getElementById("cart-form") ||
      document.querySelector('[action="/cart"]');
    if (!target) {
      return;
    }

    // Observe the nearest section wrapper so we catch full section replacements
    var section = target.closest(".shopify-section") || target.parentNode;

    var debounceTimer;
    var observer = new MutationObserver(function () {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function () {
        init();
      }, 100);
    });

    observer.observe(section, { childList: true, subtree: true });
  }

  function boot() {
    init();
    observeCartForm();
    
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();


// /**
//  * Cart Enhancements for Pinnacle/Bastion Theme
//  * - Syncs drop location select to Shopify cart attributes
//  * - Adds a delivery date calendar picker (Flatpickr)
//  * - Blocks Alberta statutory holidays automatically
//  * - Persists all custom fields into cart attributes so they appear on the order
//  *
//  * Dependencies:
//  *   Flatpickr CSS + JS (loaded automatically if not already present)
//  *
//  * Usage:
//  *   Include this script on the cart page after the cart form:
//  *   <script src="{{ 'cart-form.js' | asset_url }}" defer></script>
//  */

// (function () {
//   "use strict";

//   /* -------------------------------------------------------------------------
//    * Config
//    * ---------------------------------------------------------------------- */

//   var FLATPICKR_CSS =
//     "https://cdn.jsdelivr.net/npm/flatpickr/dist/flatpickr.min.css";
//   var FLATPICKR_JS = "https://cdn.jsdelivr.net/npm/flatpickr";

//   // Minimum number of days from today before a delivery can be scheduled
//   var MIN_LEAD_DAYS = 2;

//   // Days of the week the store delivers (0 = Sunday, 1 = Mon … 6 = Sat)
//   var DELIVERY_DAYS = [1, 2, 3, 4, 5, 6];

//   // Maximum number of days into the future customers can book.
//   // Set to 0 or false to allow unlimited future dates.
//   var MAX_FUTURE_DAYS = 0;

//   /* -------------------------------------------------------------------------
//    * Alberta Statutory Holidays (computed automatically each year)
//    * ---------------------------------------------------------------------- */

//   function nthWeekday(year, month, dayOfWeek, n) {
//     var date = new Date(year, month, 1);
//     var count = 0;
//     while (count < n) {
//       if (date.getDay() === dayOfWeek) {
//         count++;
//       }
//       if (count < n) {
//         date.setDate(date.getDate() + 1);
//       }
//     }
//     return date;
//   }

//   function getGoodFriday(year) {
//     var a = year % 19;
//     var b = Math.floor(year / 100);
//     var c = year % 100;
//     var d = Math.floor(b / 4);
//     var e = b % 4;
//     var f = Math.floor((b + 8) / 25);
//     var g = Math.floor((b - f + 1) / 3);
//     var h = (19 * a + b - d - g + 15) % 30;
//     var i = Math.floor(c / 4);
//     var k = c % 4;
//     var l = (32 + 2 * e + 2 * i - h - k) % 7;
//     var m = Math.floor((a + 11 * h + 22 * l) / 451);
//     var month = Math.floor((h + l - 7 * m + 114) / 31) - 1;
//     var day = ((h + l - 7 * m + 114) % 31) + 1;
//     var easter = new Date(year, month, day);
//     easter.setDate(easter.getDate() - 2);
//     return easter;
//   }

//   function pad(n) {
//     return n < 10 ? "0" + n : "" + n;
//   }

//   function fmtDate(d) {
//     return (
//       d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate())
//     );
//   }

//   function getAlbertaHolidays(year) {
//     var holidays = [];
//     var may25, victoria;

//     // New Year's Day
//     holidays.push(fmtDate(new Date(year, 0, 1)));
//     // Family Day — 3rd Monday of February
//     holidays.push(fmtDate(nthWeekday(year, 1, 1, 3)));
//     // Good Friday
//     holidays.push(fmtDate(getGoodFriday(year)));
//     // Victoria Day — last Monday before May 25
//     may25 = new Date(year, 4, 25);
//     victoria = new Date(may25);
//     while (victoria.getDay() !== 1) {
//       victoria.setDate(victoria.getDate() - 1);
//     }
//     holidays.push(fmtDate(victoria));
//     // Canada Day
//     holidays.push(fmtDate(new Date(year, 6, 1)));
//     // Heritage Day — 1st Monday of August
//     holidays.push(fmtDate(nthWeekday(year, 7, 1, 1)));
//     // Labour Day — 1st Monday of September
//     holidays.push(fmtDate(nthWeekday(year, 8, 1, 1)));
//     // National Day for Truth and Reconciliation
//     holidays.push(fmtDate(new Date(year, 8, 30)));
//     // Thanksgiving — 2nd Monday of October
//     holidays.push(fmtDate(nthWeekday(year, 9, 1, 2)));
//     // Remembrance Day
//     holidays.push(fmtDate(new Date(year, 10, 11)));
//     // Christmas Day
//     holidays.push(fmtDate(new Date(year, 11, 25)));

//     return holidays;
//   }

//   function buildHolidaySet() {
//     var thisYear = new Date().getFullYear();
//     var all = getAlbertaHolidays(thisYear).concat(
//       getAlbertaHolidays(thisYear + 1),
//     );
//     var set = {};
//     var i;
//     for (i = 0; i < all.length; i++) {
//       set[all[i]] = true;
//     }
//     return set;
//   }

//   var holidaySet = buildHolidaySet();

//   function isHoliday(date) {
//     return !!holidaySet[fmtDate(date)];
//   }

//   /* -------------------------------------------------------------------------
//    * Helpers
//    * ---------------------------------------------------------------------- */

//   function updateCartAttributes(attributes) {
//     return fetch("/cart/update.js", {
//       method: "POST",
//       headers: { "Content-Type": "application/json" },
//       body: JSON.stringify({ attributes: attributes }),
//     }).then(function (res) {
//       if (!res.ok) {
//         throw new Error("Cart update failed: " + res.status);
//       }
//       return res.json();
//     });
//   }

//   function loadCSS(href) {
//     if (document.querySelector('link[href="' + href + '"]')) {
//       return;
//     }
//     var link = document.createElement("link");
//     link.rel = "stylesheet";
//     link.href = href;
//     document.head.appendChild(link);
//   }

//   function loadScript(src) {
//     return new Promise(function (resolve, reject) {
//       if (document.querySelector('script[src="' + src + '"]')) {
//         resolve();
//         return;
//       }
//       var script = document.createElement("script");
//       script.src = src;
//       script.onload = resolve;
//       script.onerror = reject;
//       document.head.appendChild(script);
//     });
//   }

//   /* -------------------------------------------------------------------------
//    * Drop Location — connect the existing <select> to cart attributes
//    * ---------------------------------------------------------------------- */

//   function initDropLocation() {
//     var select = document.getElementById("CartDropLocation");
//     if (!select) {
//       return;
//     }

//     select.name = "attributes[Drop Location]";

//     select.addEventListener("change", function () {
//       var value = select.value;
//       if (value === "Select a Location") {
//         value = "";
//       }
//       updateCartAttributes({ "Drop Location": value });
//     });
//   }

//   /* -------------------------------------------------------------------------
//    * Delivery Date — inject a date picker after the drop location select
//    * ---------------------------------------------------------------------- */

//   function buildDeliveryDateUI() {
//     var anchor = document.getElementById("CartDropLocation");
//     if (!anchor) {
//       return null;
//     }

//     var helperText = anchor.nextElementSibling;
//     while (helperText && helperText.tagName !== "P") {
//       helperText = helperText.nextElementSibling;
//     }
//     var insertAfter = helperText || anchor;

//     var label = document.createElement("label");
//     label.setAttribute("for", "CartDeliveryDate");
//     label.className = "thb-cart-form--notes-title";
//     label.textContent = "Preferred delivery date *";

//     var input = document.createElement("input");
//     input.type = "text";
//     input.id = "CartDeliveryDate";
//     input.name = "attributes[Delivery Date]";
//     input.className = "full";
//     input.placeholder = "Select a delivery date";
//     input.readOnly = true;
//     input.style.cssText = "width:100%;cursor:pointer;margin-bottom:20px;";

//     insertAfter.parentNode.insertBefore(input, insertAfter.nextSibling);
//     insertAfter.parentNode.insertBefore(label, input);

//     return input;
//   }

//   function initDeliveryDate() {
//     var input = buildDeliveryDateUI();
//     if (!input) {
//       return;
//     }

//     var minDate = new Date();
//     minDate.setDate(minDate.getDate() + MIN_LEAD_DAYS);

//     loadCSS(FLATPICKR_CSS);

//     loadScript(FLATPICKR_JS).then(function () {
//       var options = {};

//       options.dateFormat = "F j, Y";
//       options.minDate = minDate;
//       options.disableMobile = true;

//       options.disable = [
//         function (date) {
//           if (DELIVERY_DAYS.indexOf(date.getDay()) === -1) {
//             return true;
//           }
//           if (isHoliday(date)) {
//             return true;
//           }
//           return false;
//         },
//       ];

//       options.onChange = function (selectedDates, dateStr) {
//         updateCartAttributes({ "Delivery Date": dateStr });
//       };

//       if (MAX_FUTURE_DAYS) {
//         options.maxDate = new Date().fp_incr(MAX_FUTURE_DAYS);
//       }

//       flatpickr(input, options);
//     });
//   }

//   /* -------------------------------------------------------------------------
//    * Form validation — block checkout if required fields are empty
//    * ---------------------------------------------------------------------- */

//   function initValidation() {
//     var form = document.getElementById("cart-form");
//     if (!form) {
//       return;
//     }

//     var checkoutButtons = document.querySelectorAll('[name="checkout"]');

//     checkoutButtons.forEach(function (btn) {
//       btn.addEventListener("click", function (e) {
//         var dropLocation = document.getElementById("CartDropLocation");
//         var deliveryDate = document.getElementById("CartDeliveryDate");
//         var messages = [];

//         if (
//           dropLocation &&
//           (!dropLocation.value || dropLocation.value === "Select a Location")
//         ) {
//           messages.push("Please select a drop location.");
//         }
//         if (deliveryDate && !deliveryDate.value) {
//           messages.push("Please select a preferred delivery date.");
//         }

//         if (messages.length) {
//           e.preventDefault();
//           alert(messages.join("\n"));
//         }
//       });
//     });
//   }

//   /* -------------------------------------------------------------------------
//    * Boot
//    * ---------------------------------------------------------------------- */

//   function init() {
//     initDropLocation();
//     initDeliveryDate();
//     initValidation();
//   }

//   if (document.readyState === "loading") {
//     document.addEventListener("DOMContentLoaded", init);
//   } else {
//     init();
//   }
// })();
